// ─────────────────────────────────────────────────────────────────────────────
// BottomSheet — MIDNIGHT primitive
//
// The #1 modal pattern for the redesigned platform. Slides up from the bottom
// on mobile with a grab handle, dimmed+blurred backdrop, and optional drag-to-
// dismiss gesture. On desktop (≥768px) it falls back to a centered glass card.
//
// Z-ordering is coordinated via the `useSheetStack()` hook so nested sheets
// always stack correctly (root sheet: z-200; each nested sheet adds 10).
//
// USAGE
// ─────
//   import { BottomSheet } from '@/components/ui/BottomSheet'
//
//   <BottomSheet
//     open={open}
//     onClose={() => setOpen(false)}
//     title="Move to stage"
//     description="Pick the stage to move this record to."
//     footer={
//       <>
//         <button className="…">Cancel</button>
//         <button className="…">Confirm</button>
//       </>
//     }
//   >
//     <YourContent />
//   </BottomSheet>
//
//   // Snap points (partial support — currently used for initialSnap heights)
//   <BottomSheet open snapPoints={['auto', 'half', 'full']} initialSnap="half">
//     …
//   </BottomSheet>
//
//   // Nested sheet — wrap your app in <SheetStackProvider> once so this
//   // component can auto-resolve a higher z-index when opened from another
//   // sheet. Import from '@/hooks/useSheetStack'.
// ─────────────────────────────────────────────────────────────────────────────

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import FocusLock from 'focus-trap-react'
import { useDrag } from '@use-gesture/react'
import { useSheetStack } from '@/hooks/useSheetStack'
import { useKeyboardAvoidance } from '@/hooks/useKeyboardAvoidance'

// ─── types ──────────────────────────────────────────────────────────────────

export type SheetSnap = 'auto' | 'half' | 'full'

/**
 * Desktop card width preset. Mobile sheets always span full viewport width,
 * so this only affects the desktop ≥768px centered-card variant. Defaults
 * to `md` which is the historical 520px.
 */
export type SheetSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

export interface BottomSheetProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  /** Pinned below the scrollable content, above the safe-area inset. */
  footer?: ReactNode
  /** Allowed snap heights. Defaults to `['auto']`. */
  snapPoints?: SheetSnap[]
  /** Initial snap (must be present in `snapPoints`). Defaults to the first. */
  initialSnap?: SheetSnap
  /** Allow close via swipe / backdrop tap / Esc. Default true. */
  dismissible?: boolean
  /** Show the iOS-style grab handle at the top. Default true. */
  showHandle?: boolean
  /** Fires whenever the sheet re-snaps to a new point. */
  onSnapChange?: (snap: SheetSnap) => void
  /** Desktop card width preset. Mobile spans full width regardless. Default `md` (520px). */
  size?: SheetSize
  className?: string
  /** Override z-index. Normally resolved from `useSheetStack()`. */
  zIndex?: number
}

// Desktop card widths. `min(<width>, calc(100vw - 32px))` so a small viewport
// never overflows. `full` matches the historical Modal `max-w-4xl` (896px).
const DESKTOP_WIDTHS: Record<SheetSize, string> = {
  sm:   'min(420px, calc(100vw - 32px))',
  md:   'min(520px, calc(100vw - 32px))',
  lg:   'min(640px, calc(100vw - 32px))',
  xl:   'min(720px, calc(100vw - 32px))',
  full: 'min(960px, calc(100vw - 32px))',
}

// ─── module-level scroll lock (refcounted across nested sheets) ────────────

let scrollLockCount = 0
let restoreOverflow = ''

function acquireScrollLock() {
  if (typeof document === 'undefined') return
  if (scrollLockCount === 0) {
    restoreOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  scrollLockCount += 1
}

function releaseScrollLock() {
  if (typeof document === 'undefined') return
  scrollLockCount = Math.max(0, scrollLockCount - 1)
  if (scrollLockCount === 0) {
    document.body.style.overflow = restoreOverflow
    restoreOverflow = ''
  }
}

// ─── misc helpers ───────────────────────────────────────────────────────────

function snapHeightCss(snap: SheetSnap): string {
  // Use dvh (dynamic viewport height) so the sheet doesn't sit underneath
  // the iOS Safari URL bar / bottom toolbar. vh would over-extend on iOS
  // and the user would have to scroll to see the footer (FND-096).
  // Reads --safe-top from index.css to avoid hand-rolling env() (CLAUDE.md §8).
  switch (snap) {
    case 'full':
      return 'calc(100dvh - var(--safe-top) - 12px)'
    case 'half':
      return '50dvh'
    case 'auto':
    default:
      return 'auto'
  }
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function isDesktop(): boolean {
  if (typeof window === 'undefined') return false
  const byWidth = window.innerWidth >= 768
  const byPointer =
    !!window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  return byWidth || byPointer
}

// ─── component ──────────────────────────────────────────────────────────────

export function BottomSheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  snapPoints = ['auto'],
  initialSnap,
  dismissible = true,
  showHandle = true,
  onSnapChange,
  size = 'md',
  className,
  zIndex: zIndexOverride,
}: BottomSheetProps) {
  const { zIndex: stackZIndex, push, pop } = useSheetStack()
  const zIndex = zIndexOverride ?? stackZIndex

  const sheetRef = useRef<HTMLDivElement | null>(null)
  const titleId = useId()
  const descId = useId()
  const keyboardHeight = useKeyboardAvoidance()

  // ── desktop fallback state (re-evaluates on resize + orientationchange) ───
  const [desktop, setDesktop] = useState<boolean>(() => isDesktop())
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = () => setDesktop(isDesktop())
    // iOS fires orientationchange before innerWidth settles — defer one frame
    const onOrientationChange = () => requestAnimationFrame(handler)
    window.addEventListener('resize', handler, { passive: true })
    window.addEventListener('orientationchange', onOrientationChange, { passive: true })
    return () => {
      window.removeEventListener('resize', handler)
      window.removeEventListener('orientationchange', onOrientationChange)
    }
  }, [])

  // ── reduced motion (captured once per mount, re-checked if pref changes)
  const [reducedMotion, setReducedMotion] = useState<boolean>(() =>
    prefersReducedMotion(),
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handler = () => setReducedMotion(mq.matches)
    mq.addEventListener?.('change', handler)
    return () => mq.removeEventListener?.('change', handler)
  }, [])

  // ── snap handling ───────────────────────────────────────────────────────
  const allowedSnaps = useMemo<SheetSnap[]>(
    () => (snapPoints.length > 0 ? snapPoints : ['auto']),
    [snapPoints],
  )
  const [snap, setSnap] = useState<SheetSnap>(() => {
    if (initialSnap && allowedSnaps.includes(initialSnap)) return initialSnap
    return allowedSnaps[0]
  })
  useEffect(() => {
    // Reset snap whenever the sheet re-opens.
    if (!open) return
    const next =
      initialSnap && allowedSnaps.includes(initialSnap)
        ? initialSnap
        : allowedSnaps[0]
    setSnap(next)
  }, [open, initialSnap, allowedSnaps])

  const handleSnapChange = useCallback(
    (next: SheetSnap) => {
      setSnap(next)
      onSnapChange?.(next)
    },
    [onSnapChange],
  )

  // ── lifecycle: push/pop the stack, lock body scroll, esc key ────────────
  const openRef = useRef(open)
  openRef.current = open
  useLayoutEffect(() => {
    if (!open) return
    push()
    acquireScrollLock()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible && openRef.current) {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      releaseScrollLock()
      pop()
    }
    // onClose/dismissible intentionally read via closure — we want a single
    // push/pop cycle per open transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ── drag-to-dismiss (mobile only) ───────────────────────────────────────
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const dragBind = useDrag(
    ({ last, velocity: [, vy], movement: [, my], cancel }) => {
      if (!dismissible || desktop) {
        cancel?.()
        return
      }
      if (last) {
        setIsDragging(false)
        const shouldDismiss = vy > 0.5 || my > 100
        if (shouldDismiss) {
          onClose()
        } else {
          setDragY(0) // spring back
        }
      } else {
        setIsDragging(true)
        // Apply resistance when dragging up (negative my). When dragging down
        // (positive my), no resistance — 1:1 follow.
        if (my < 0) {
          setDragY(-Math.sqrt(Math.min(-my, 400)) * 4)
        } else {
          setDragY(my)
        }
      }
    },
    {
      axis: 'y',
      filterTaps: true,
      pointer: { touch: true },
      enabled: open && dismissible && !desktop,
    },
  )

  // Swallow drag binding type to plain object spread for JSX.
  const gestureHandlers = dragBind()

  // ── visuals ─────────────────────────────────────────────────────────────
  if (!open || typeof document === 'undefined') return null

  const enterDuration = reducedMotion ? 150 : 400
  const backdropDuration = reducedMotion ? 150 : 250
  const ease = 'cubic-bezier(0.2, 0.8, 0.2, 1)'

  // MIDNIGHT tokens with fallback hex (Tokens agent will land CSS vars separately).
  const sheetBg = 'var(--midnight-sheet-bg, #0A0A0C)'
  const hairline = 'var(--midnight-border, rgba(255,255,255,0.08))'
  const tileBg = 'var(--midnight-tile, rgba(255,255,255,0.04))'
  const radius2xl = 'var(--midnight-r-2xl, 40px)'
  const radiusXl = 'var(--midnight-r-xl, 28px)'
  const radiusPill = 'var(--midnight-r-pill, 9999px)'

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: zIndex - 1,
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    opacity: isDragging ? Math.max(0.2, 1 - dragY / 600) : 1,
    transition: isDragging
      ? 'none'
      : `opacity ${backdropDuration}ms ${ease}`,
    animation: reducedMotion
      ? `floRiteSheetFadeIn ${backdropDuration}ms ${ease}`
      : `floRiteSheetFadeIn ${backdropDuration}ms ${ease}`,
    cursor: dismissible ? 'pointer' : 'default',
  }

  const baseSheetStyle: CSSProperties = {
    position: 'fixed',
    zIndex,
    background: sheetBg,
    boxShadow: '0 -20px 60px rgba(0,0,0,0.7)',
    display: 'flex',
    flexDirection: 'column',
    color: '#FFFFFF',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', system-ui, sans-serif",
  }

  // Shape rule (Gaige, 2026-05-16): NEVER rounded-top + square-bottom.
  // Mobile sheets are rounded on ALL FOUR corners so the bottom edge is
  // visually finished. The sheet sits flush at bottom: 0 — the bottom
  // corners curve against the dimmed backdrop, and the footer keeps its
  // safe-area padding so content clears the home indicator. The whole
  // perimeter gets a hairline border (not just the top) for the same
  // reason: a square bottom edge would betray the rounded shell.
  const mobileSheetStyle: CSSProperties = {
    ...baseSheetStyle,
    left: 0,
    right: 0,
    bottom: keyboardHeight > 0 ? keyboardHeight : 0,
    borderRadius: radius2xl,
    border: `0.5px solid ${hairline}`,
    maxHeight: keyboardHeight > 0
      ? `calc(100dvh - ${keyboardHeight}px - var(--safe-top) - 12px)`
      : snap === 'full' ? snapHeightCss('full') : '85dvh',
    height: snap === 'auto' ? 'auto' : snapHeightCss(snap),
    transform: `translateY(${dragY}px)`,
    transition: isDragging
      ? 'none'
      : `transform ${enterDuration}ms ${ease}`,
    animation: reducedMotion
      ? `floRiteSheetFadeIn ${enterDuration}ms ${ease}`
      : `floRiteSheetSlideUp ${enterDuration}ms ${ease}`,
    touchAction: 'none',
    overflow: 'hidden',
  }

  // Desktop centered card — all four corners equal (radiusXl = 28px) and
  // overflow:hidden so the rounded mask clips inner content. Matches the
  // shape rule documented above mobileSheetStyle.
  const desktopSheetStyle: CSSProperties = {
    ...baseSheetStyle,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: DESKTOP_WIDTHS[size],
    maxHeight: 'min(80vh, 720px)',
    borderRadius: radiusXl,
    border: `0.5px solid ${hairline}`,
    overflow: 'hidden',
    animation: reducedMotion
      ? `floRiteSheetFadeIn ${backdropDuration}ms ${ease}`
      : `floRiteSheetScaleIn ${enterDuration}ms ${ease}`,
  }

  const sheetStyle = desktop ? desktopSheetStyle : mobileSheetStyle

  const handleStyle: CSSProperties = {
    width: 36,
    height: 5,
    borderRadius: radiusPill,
    background: 'rgba(255,255,255,0.22)',
    margin: '20px auto 0',
    flexShrink: 0,
    touchAction: 'none',
  }

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: (showHandle && !desktop) ? '14px 20px 8px' : '20px 20px 8px',
    flexShrink: 0,
  }

  const titleWrapStyle: CSSProperties = {
    minWidth: 0,
    flex: 1,
  }

  const titleStyle: CSSProperties = {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
    margin: 0,
    color: '#FFFFFF',
  }

  const descStyle: CSSProperties = {
    fontSize: 14,
    color: 'rgba(255,255,255,0.60)',
    marginTop: 6,
  }

  const closeBtnStyle: CSSProperties = {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: radiusPill,
    border: `0.5px solid ${hairline}`,
    background: tileBg,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    color: 'rgba(255,255,255,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  }

  const bodyStyle: CSSProperties = {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '8px 20px 20px',
  }

  const footerStyle: CSSProperties = {
    flexShrink: 0,
    padding: keyboardHeight > 0 && !desktop
      ? '16px 20px 16px 20px'
      : desktop
      ? '16px 20px 20px 20px'
      // var(--safe-bottom) from index.css — canonical safe-area access (CLAUDE.md §8).
      : '16px 20px calc(var(--safe-bottom) + 16px) 20px',
    borderTop: `0.5px solid ${hairline}`,
    background: sheetBg,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  }

  const ariaLabelledBy = title ? titleId : undefined
  const ariaDescribedBy = description ? descId : undefined

  const handleBackdropClick = () => {
    if (dismissible) onClose()
  }

  const content = (
    <>
      {/* keyframes scoped via constant id — safe to inline multiple times */}
      <style>{`
        @keyframes floRiteSheetFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes floRiteSheetSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes floRiteSheetScaleIn {
          from { opacity: 0; transform: translate(-50%, calc(-50% + 12px)) scale(0.96); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>

      <div
        aria-hidden="true"
        onClick={handleBackdropClick}
        style={backdropStyle}
      />

      <FocusLock returnFocusOnDeactivate={true} active={open} focusTrapOptions={{ allowOutsideClick: true }}>
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          data-modal-size={size}
          data-modal-variant={desktop ? 'desktop' : 'mobile'}
          className={className}
          style={sheetStyle}
        >
          {showHandle && !desktop && (
            <div
              {...(gestureHandlers as Record<string, unknown>)}
              aria-label="Drag to dismiss"
              role="button"
              tabIndex={-1}
              style={handleStyle}
            />
          )}

          {(title || description) ? (
            <div
              {...(!desktop && showHandle ? {} : (gestureHandlers as Record<string, unknown>))}
              style={headerStyle}
            >
              <div style={titleWrapStyle}>
                {title ? (
                  <h2 id={titleId} style={titleStyle}>
                    {title}
                  </h2>
                ) : null}
                {description ? (
                  <div id={descId} style={descStyle}>
                    {description}
                  </div>
                ) : null}
              </div>
              {dismissible ? (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  style={closeBtnStyle}
                >
                  <X size={18} />
                </button>
              ) : null}
            </div>
          ) : (
            dismissible && (
              <div style={{ ...headerStyle, justifyContent: 'flex-end', padding: '8px 12px 0' }}>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  style={closeBtnStyle}
                >
                  <X size={18} />
                </button>
              </div>
            )
          )}

          <div style={bodyStyle}>{children}</div>

          {footer ? <div style={footerStyle}>{footer}</div> : null}

          {/* Silence snap-change lint — the state is consumed via onSnapChange. */}
          <SnapSideEffect snap={snap} onChange={handleSnapChange} />
        </div>
      </FocusLock>
    </>
  )

  return createPortal(content, document.body)
}

// Tiny helper so we can call onSnapChange only when snap actually changes,
// without adding effect noise to the main component body.
function SnapSideEffect({
  snap,
  onChange,
}: {
  snap: SheetSnap
  onChange: (s: SheetSnap) => void
}) {
  const prev = useRef<SheetSnap>(snap)
  useEffect(() => {
    if (prev.current !== snap) {
      prev.current = snap
      onChange(snap)
    }
  }, [snap, onChange])
  return null
}

export default BottomSheet
